import { ChartType, Plugin } from "chart.js";
import { GRAPH_DEFAULTS, MOTIVES_TYPES } from "../Enums/index.js";
import skipTicks, { getEffectiveSize } from "../Plugins/skipTicks.js";
import {
	dimensionRenderMode,
	offsetDateGranularity,
	offsetSupportsPeriodTicks,
} from "../graphUtils.js";
import { DEFAULT_TICK_PADDING, formatTickLabel } from "../Scales/xScale.js";
import DocumentDO = App.Data.Explorer.DocumentDO;

export const PLUGIN_KEY = "$secondLevelLabels";

type LabelToDraw = {
	text: string;
	x: number;
	y: number;
	textAlign: "center" | "end";
};

// Define the plugin options interface
interface SecondLevelLabelsOptions {
	hasOffset: boolean;
	document: DocumentDO;
}

declare module "chart.js" {
	interface PluginOptionsByType<TType extends ChartType> {
		secondLevelLabels: SecondLevelLabelsOptions;
	}
}

function setupContext(ctx: CanvasRenderingContext2D) {
	const fontOptions = {
		family: undefined,
		size: 12,
		style: "normal",
		weight: "normal",
		lineHeight: 1.2,
	};

	// Set up the font
	ctx.font = fontString(
		fontOptions.size,
		fontOptions.style,
		fontOptions.weight,
		fontOptions.family
	);
	ctx.fillStyle = GRAPH_DEFAULTS.TEXT_COLOR;
	ctx.textAlign = "center";
	ctx.textBaseline = "top";
}

// Define our plugin
const secondLevelLabelsPlugin: Plugin<ChartType, SecondLevelLabelsOptions> = {
	id: "secondLevelLabels",

	defaults: {
		hasOffset: false,
	},

	beforeInit(chart, args, options) {
		chart[PLUGIN_KEY] = {
			labels: [] as LabelToDraw[],
			maxHeight: DEFAULT_TICK_PADDING,
			hideOffset: false,
		};
	},

	afterUpdate(chart, args, options) {
		chart[PLUGIN_KEY].labels = [];
		const { ctx, scales } = chart;
		if (!options.hasOffset) {
			return;
		}

		const uniqueLabels = [
			...new Set(chart.data.datasets.map((dataset) => dataset.stack)),
		];

		// Make sure we have x scale and labels
		if (!scales.x || !uniqueLabels.length) {
			return;
		}

		const renderMode = dimensionRenderMode(options.document.offset_dimension);

		const scaleWidth = scales.x.width;
		const tickSize = scaleWidth / chart.data.labels.length;

		const isRotated =
			options.document.is_horizontal_label_rotation_enabled &&
			options.document.horizontal_label_rotation_degrees != 0;

		ctx.save();
		setupContext(ctx);

		const baseY = chart.scales.y.getPixelForTick(0);

		const cachedWidths = [];
		let maxHeightForUpdate = DEFAULT_TICK_PADDING;

		const possibleLabels: Record<number, any[]> = {};
		let widestLabelWidth = 0;
		let approxDigitWidth = 1;
		let maxLabelDigits = 0;
		for (
			let chartLabelIdx = 0;
			chartLabelIdx < chart.data.labels.length;
			chartLabelIdx++
		) {
			const hasDrawn = {};

			// Usually things such as capacity (among other things) shouldn't cause another render of the label
			// Though, if the dataset is hidden, but has capacity, its label should
			const skippedOverlay = {};

			chart.data.datasets.forEach((dataset, datasetIndex) => {
				if (hasDrawn[dataset.stack]) {
					return;
				}

				if (
					[
						MOTIVES_TYPES.MAX_OCCUPANCY,
						MOTIVES_TYPES.MIN_OCCUPANCY,
						MOTIVES_TYPES.LINE,
					].includes(dataset.motivesType) ||
					(dataset.motivesType === MOTIVES_TYPES.CAPACITY &&
						options.document.graph_type !== "bar")
				) {
					if (!dataset.hidden) {
						skippedOverlay[dataset.stack] = true;
					}
					return;
				}

				if (dataset.hidden && !skippedOverlay[dataset.stack]) {
					return;
				}

				if (skippedOverlay[dataset.stack]) {
					skippedOverlay[dataset.stack] = false;
				}

				hasDrawn[dataset.stack] = true;

				const textToDraw = formatTickLabel(
					dataset.stack!,
					renderMode,
					100, // limiting is done later on
					options.document,
					{
						isOffsetLabel: true,
					}
				);

				const meta = chart.getDatasetMeta(datasetIndex);

				const el = meta.data[chartLabelIdx];
				if (possibleLabels[chartLabelIdx] === undefined) {
					possibleLabels[chartLabelIdx] = [];
				}

				const width =
					cachedWidths[textToDraw] ||
					(cachedWidths[textToDraw] = ctx.measureText(textToDraw).width);

				if (el?.x) {
					possibleLabels[chartLabelIdx].push({
						text: textToDraw,
						value: dataset.stack,
						x: el.x,
						y: baseY + 8,
						width,
						maxWidth: el.getProps(["width"], true).width,
					});
				}

				if (width > widestLabelWidth) {
					widestLabelWidth = width;
					maxLabelDigits = textToDraw.length;

					if (isRotated) {
						approxDigitWidth = width / textToDraw.length;
					}
				}
			});
		}

		const fontWidth =
			approxDigitWidth > 1
				? approxDigitWidth
				: scales.x._resolveTickFontOptions(0).lineHeight;

		if (Object.entries(possibleLabels).length === 0) {
			ctx.restore();

			return;
		}

		let availableWidth = tickSize / possibleLabels[0].length;
		if (isRotated) {
			availableWidth = Math.min(125, widestLabelWidth);
		}

		// Max height for update is the height that the offset labels are taking.
		// This is then used for applying padding to the regular horizontal labels.
		// Only use this when rotating.
		maxHeightForUpdate = isRotated
			? getEffectiveSize(
					availableWidth,
					fontWidth,
					options.document.horizontal_label_rotation_degrees
			  ).effectiveHeight + 10 // include some additional padding
			: DEFAULT_TICK_PADDING; // Default to DEFAULT_TICK_PADDING to not trigger unnecessary updates.

		if (maxHeightForUpdate != chart[PLUGIN_KEY].maxHeight) {
			ctx.restore();

			chart[PLUGIN_KEY].maxHeight = maxHeightForUpdate;
			chart.options.scales.x.ticks.padding = chart[PLUGIN_KEY].maxHeight;
			chart[PLUGIN_KEY].labels = [];
			chart.update("none");

			return;
		}

		const rotatedFontWidth =
			getEffectiveSize(
				fontWidth,
				fontWidth,
				options.document.horizontal_label_rotation_degrees
			).effectiveWidth + 2; // Include some horizontal padding. Without it the labels are very close to each-other before being culled.

		const maxDigits = availableWidth / fontWidth;
		const maxSubTicks = Math.floor(
			tickSize / (isRotated ? rotatedFontWidth * 2 : widestLabelWidth + 10) // add 10 padding
		);

		if (
			maxLabelDigits > maxDigits &&
			options.document.offset_dimension.id ===
				options.document.legend_dimension?.id
		) {
			return;
		}

		// We're done computing text widths.
		ctx.restore();

		chart[PLUGIN_KEY].labels = [];
		let widthCounter = 0;
		for (const labels of Object.values(possibleLabels)) {
			const appliedLogic = skipTicks(
				labels,
				(tick) => tick.value,
				(tick, keep, isTime) => {
					if (keep) {
						chart[PLUGIN_KEY].labels.push({
							text: tick.text,
							x: tick.x,
							y: tick.y,
							textAlign: "center",
						});
					}
				},
				maxSubTicks,
				offsetDateGranularity(options.document),
				offsetSupportsPeriodTicks(options.document)
			);

			if (appliedLogic) {
				continue;
			}

			// When there is not enough space to show 3 digits each, we start removing labels.
			// There should be a minimum of 3 characters shown, however, if even less space is available we use whatever is possible
			const maxDigitsWhenCulling = Math.min(
				3,
				Math.floor(tickSize / fontWidth)
			);

			const maxWidthPerTick = tickSize;
			let sizePerTick = 0;
			if (isRotated) {
				// When rotated we only need to take the rotated width into account
				sizePerTick = rotatedFontWidth * 2 * labels.length;
			} else if (maxDigits < 3) {
				// When we apply culling logic we compute the width of the culled label
				sizePerTick = maxDigitsWhenCulling * fontWidth * labels.length;
			} else {
				// Otherwise we just set it to maxWidthPerSubTick exactly.
				// This prevents the culling logic from activating (as the condition is sizePerTick > maxWidthPerTick)
				sizePerTick = maxWidthPerTick;
			}

			labels.forEach((tick) => {
				let keep = true;
				if (sizePerTick > maxWidthPerTick) {
					keep = widthCounter === 0;
					widthCounter += maxWidthPerTick;

					if (widthCounter >= sizePerTick) {
						widthCounter = 0;
					}
				}

				if (!keep) return;

				chart[PLUGIN_KEY].labels.push({
					text: tick.text.substring(
						0,
						maxDigits < 3 ? maxDigitsWhenCulling : Math.ceil(maxDigits)
					),
					x: tick.x,
					y: tick.y,
					textAlign: "center",
				});
			});
		}
	},

	afterDraw(chart, args, options): void {
		const { ctx } = chart;

		if (
			!options.hasOffset ||
			chart[PLUGIN_KEY] === undefined ||
			!chart[PLUGIN_KEY].labels.length
		) {
			return;
		}

		ctx.save();
		setupContext(ctx);

		for (const labelToDraw of chart[PLUGIN_KEY].labels as LabelToDraw[]) {
			ctx.save();

			ctx.translate(labelToDraw.x, labelToDraw.y);

			if (options.document.is_horizontal_label_rotation_enabled) {
				ctx.rotate(
					options.document.horizontal_label_rotation_degrees * (Math.PI / 180)
				);
			}

			ctx.textAlign =
				options.document.is_horizontal_label_rotation_enabled &&
				options.document.horizontal_label_rotation_degrees !== 0
					? "end"
					: "center";
			ctx.textBaseline =
				options.document.is_horizontal_label_rotation_enabled &&
				options.document.horizontal_label_rotation_degrees !== 0
					? "middle"
					: "top";
			ctx.fillText(labelToDraw.text, 0, 0);

			ctx.restore();
		}

		ctx.restore();
	},
};

// Helper function to create font string
function fontString(
	size: number = 12,
	style?: string,
	weight?: string | number,
	family?: string
): string {
	return `${style || "normal"} ${weight || "normal"} ${size}px ${
		family || "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif"
	}`;
}

export default secondLevelLabelsPlugin;
